import {
  ApplicationResult,
  ApplyJobParams,
  type ContractTypeFilter,
  EmploymentType,
  type Job,
  JobsResponse,
  type JobSearchParams,
  JobApplication,
  JOBS_API_TIMEOUT_MS,
} from '@cibli/shared';

// VITE_API_URL may or may not include the NestJS /api prefix depending on the
// deployment — API_BASE normalizes it once (same helper as network-speed) so
// the stream URL never doubles it (/api/api/).
import { API_BASE } from '@/lib/network-speed';

import api, { getCurrentLanguage } from './api';

/**
 * Recherche d'emplois basée sur un CV
 * Note: Le nouveau endpoint ne supporte plus la pagination (limit/offset)
 * Retourne directement un tableau de Job[]
 * @param cvId - ID du CV
 * @param boothId - ID de la cabine (optionnel)
 * @param search - Mots-clés de recherche (optionnel)
 * @param city - Ville de recherche (optionnel)
 * @param contractTypes - Types de contrat à filtrer (optionnel, ATS positions)
 * @param employmentTypes - Types d'emploi à filtrer (optionnel)
 */
export const getJobs = async (
  cvId: string,
  boothId?: string,
  search?: string,
  city?: string,
  contractTypes?: ContractTypeFilter[],
  employmentTypes?: EmploymentType[],
): Promise<JobsResponse> => {
  return await api.post<
    JobsResponse,
    {
      cvId: string;
      boothId?: string;
      search?: string;
      city?: string;
      contractTypes?: ContractTypeFilter[];
      employmentTypes?: EmploymentType[];
    }
  >(
    '/jobs',
    {
      cvId,
      ...(boothId && { boothId }),
      ...(search && search.trim() && { search: search.trim() }),
      ...(city && city.trim() && { city: city.trim() }),
      ...(contractTypes && contractTypes.length > 0 && { contractTypes }),
      ...(employmentTypes && employmentTypes.length > 0 && { employmentTypes }),
    },
    { timeout: JOBS_API_TIMEOUT_MS },
  );
};

export const applyToJob = async (job: Job, cvId: string): Promise<ApplicationResult> => {
  return await api.post<ApplicationResult, ApplyJobParams>('/jobs/apply', {
    job,
    cvId,
  });
};

export const beContacted = async (): Promise<void> => {
  return await api.post<void, undefined>('/jobs/be-contacted', undefined);
};

/**
 * Get all jobs with optional filters (AUTHENTICATED endpoint)
 * Utilise le nouveau format de recherche unifiée avec JobSearchParams
 * Retourne un tableau de Job (pas de pagination)
 */
export const getAllJobs = async (
  query: JobSearchParams = {},
  signal?: AbortSignal,
): Promise<JobsResponse> => {
  const params = Object.entries(query).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc.append(key, value.toString());
    }
    return acc;
  }, new URLSearchParams());

  const queryString = params.toString();
  return await api.get<JobsResponse>(`/jobs${queryString ? `?${queryString}` : ''}`, {
    signal,
    timeout: JOBS_API_TIMEOUT_MS,
  });
};

/**
 * Get all job applications for the authenticated user
 * Returns list of jobs the user has already applied to
 */
export const getUserJobApplications = async (): Promise<JobApplication[]> => {
  return await api.get<JobApplication[]>('/jobs/applications');
};

export type JobStreamEvent =
  | { type: 'start'; data: { sources: string[] } }
  | {
      type: 'source';
      data: {
        source: string;
        jobs: Job[];
        meta: { source: string; status: string; resultCount: number; error?: string };
      };
    }
  | { type: 'done'; data: JobsResponse };

export async function* streamJobs(
  cvId: string,
  boothId?: string,
  search?: string,
  city?: string,
  contractTypes?: ContractTypeFilter[],
  employmentTypes?: EmploymentType[],
  signal?: AbortSignal,
): AsyncGenerator<JobStreamEvent> {
  const body: Record<string, unknown> = { cvId };
  if (boothId) body.boothId = boothId;
  if (search?.trim()) body.search = search.trim();
  if (city?.trim()) body.city = city.trim();
  if (contractTypes?.length) body.contractTypes = contractTypes;
  if (employmentTypes?.length) body.employmentTypes = employmentTypes;

  const response = await fetch(`${API_BASE}/jobs/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-language': getCurrentLanguage(),
    },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Stream failed: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    let eventType = '';
    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        const rawData = line.slice(5).trim();
        if (rawData) {
          try {
            const parsed = JSON.parse(rawData);
            yield { type: parsed.type ?? eventType, data: parsed.data ?? parsed } as JobStreamEvent;
          } catch {
            // skip malformed data
          }
        }
      }
    }
  }
}
